import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { AttendanceAPI } from "@/lib/sprint5-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Check, X, Inbox } from "lucide-react";

const STATUS_BADGE = {
  pending:  "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  rejected: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20",
};

export default function Regularisations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("pending");
  const [decideTarget, setDecideTarget] = useState(null);
  const canDecide = user?.roles?.some((r) => ["super_admin", "inst_admin", "principal", "hod"].includes(r));

  const regsQuery = useQuery({
    queryKey: ["regs", filter],
    queryFn: () => AttendanceAPI.listRegs(filter === "all" ? {} : { status: filter }),
  });

  return (
    <AppShell>
      <div className="p-8" data-testid="regularisations-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Regularisations</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Requests to convert an absent record into excused. Principals
              and HoDs review and decide.
            </p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="regs-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border rounded-md bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Requested by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regsQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : !regsQuery.data?.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12" data-testid="regs-empty">
                  <Inbox className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No requests</p>
                </TableCell></TableRow>
              ) : regsQuery.data.map((r) => (
                <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.student_id.slice(-8)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell className="text-sm max-w-md truncate" title={r.reason}>{r.reason}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.requested_by_email || "—"}</TableCell>
                  <TableCell><Badge className={(STATUS_BADGE[r.status] || "") + " border capitalize"}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {canDecide && r.status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => setDecideTarget(r)} data-testid={`reg-decide-${r.id}`}>Decide</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {decideTarget && (
          <DecideDialog
            reg={decideTarget}
            onClose={() => { setDecideTarget(null); queryClient.invalidateQueries({ queryKey: ["regs"] }); }}
          />
        )}
      </div>
    </AppShell>
  );
}

function DecideDialog({ reg, onClose }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await AttendanceAPI.decideReg(reg.id, decision, notes || undefined);
      toast.success(`Request ${decision === "approve" ? "approved" : "rejected"}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="decide-dialog">
        <DialogHeader>
          <DialogTitle>Review regularisation request</DialogTitle>
          <DialogDescription>Approving converts the absent record to <b>excused</b>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border rounded-md p-3 bg-secondary/30">
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Reason</div>
            <div className="text-sm mt-1">{reg.reason}</div>
            <div className="text-[10px] font-mono text-muted-foreground mt-2">Date {reg.date} · By {reg.requested_by_email || "—"}</div>
          </div>
          <Textarea placeholder="Decision notes (optional)…" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="decide-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => decide("reject")} disabled={busy} data-testid="decide-reject"><X className="h-3.5 w-3.5 mr-1.5" />Reject</Button>
          <Button onClick={() => decide("approve")} disabled={busy} data-testid="decide-approve">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
